import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import truncate from 'lodash/truncate';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import {
  Url,
  ALL_FOLDER_ID,
  ALL_FOLDER_NAME,
  USER_PROFILE_POSTS_SUB_PATHS,
} from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import {
  NotificationQueueItem,
  Notification,
} from '@wix/da-shared-react/pkg/Notifications';
import { NotificationDuration } from '@wix/da-shared-react/pkg/Notifications/Notification';
import SystemNotification from '@wix/da-shared-react/pkg/SystemNotification';
import Dumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import { useEditDeviationClick } from '../../hooks/useEditDeviationClick';
import { StudioSection } from '../../types/store';

import s from './DeviationSubmitNotification.scss';

export interface Props extends Omit<NotificationQueueItem, 'type'> {
  activeSection: StudioSection;
  deviation?: PapiDeviation;
  deviationIds?: number[];
  isPublish?: boolean;
}

const DeviationSubmitNotification: FC<Props> = ({
  id,
  queueName,
  activeSection,
  deviation,
  deviationIds = [],
  isPublish,
}) => {
  const { t } = useTranslation();
  const { user } = useContext(SessionContext);
  const { onEditClick } = useEditDeviationClick(deviation!);
  const { username } = user as PapiUser;
  const isDraftSave = deviation && !isPublish;
  const isJournals = activeSection === StudioSection.JOURNALS;

  const customIcon = deviation ? (
    <Dumbo deviation={deviation} width={48} height={48} />
  ) : (
    <div className={s['count-icon']}>+{deviationIds.length}</div>
  );

  return (
    <Notification
      id={id}
      queueName={queueName}
      cardComponent={SystemNotification}
      cardProps={{
        className: s['notification-card'],
        textClassName: s['notification-card-text'],
        iconClassName: s['notification-card-icon'],
        customIcon,
        multiLine: true,
        limitTextWidth: false,
        duration: NotificationDuration.S,
        actions: [
          isDraftSave
            ? {
                label: t(
                  'studio.notifications.deviationSubmit.actions.editDraft'
                ),
                onClick: onEditClick,
                closeOnClick: true,
              }
            : deviation
            ? {
                label: t(
                  'studio.notifications.deviationSubmit.actions.viewDeviation'
                ),
                href: Url.deviationLink(
                  deviation.deviationId,
                  deviation.author?.username,
                  deviation.title
                ),
              }
            : isJournals
            ? {
                label: t(
                  'studio.notifications.deviationSubmit.actions.viewPosts'
                ),
                href: Url.userPostsLink({
                  username,
                  subPath: USER_PROFILE_POSTS_SUB_PATHS.ALL,
                }),
              }
            : {
                label: t(
                  'studio.notifications.deviationSubmit.actions.viewGallery'
                ),
                href: Url.userGalleryLink({
                  username,
                  gallectionType: 'gallery',
                  folderId: ALL_FOLDER_ID,
                  folderName: ALL_FOLDER_NAME,
                }),
              },
        ],
      }}
    >
      {isDraftSave
        ? t('studio.notifications.deviationSubmit.draftText', {
            title: truncate(deviation.title, { length: 18, omission: '...' }),
          })
        : deviation
          ? t('studio.notifications.deviationSubmit.text_one', {
              title: truncate(deviation.title, { length: 18, omission: '...' }),
            })
          : t('studio.notifications.deviationSubmit.text_other', {
              count: deviationIds.length,
            })}
    </Notification>
  );
};
DeviationSubmitNotification.displayName = 'DeviationSubmitNotification';

export default DeviationSubmitNotification;
